// libs
import { useCallback, useContext, useEffect, useRef, useState } from "react";

// internal-deps
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import {
    useSessionStorage,
    ApiContext,
    recordNREvent,
    NEWRELIC_TABLES,
    WP_JUSPAY_CARD_FLOW_ERROR_STATE,
} from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/payment-core";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { JuspayClientAuthTokenData, JuspaySdkFeatureAvailable } from "@payment-client/interfaces/Juspay";

// helpers
import { isJuspaySdkAvailable, resolveClientAuthTokenDataForMerchantId } from "@payment-client/helpers/JuspayHelpers";
import { isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";

// constants
import { DEFAULT_JUSPAY_SDK_STATE, SWIGGY_MONEY_JUSPAY_SDK_STATE } from "@payment-client/constants/StorageKeys";

// contexts
import { JuspayContextValue } from "@payment-client/contexts/JuspayContext";
import { getPublicJuspayClientAuthToken } from "@payment-client/apis/publicJuspay";
import { getPublicJuspayClientAuthTokensV3 } from "@payment-client/apis/publicJuspayV3";

export interface JuspaySdkState {
    isSdkAvailable?: boolean;
    clientAuthTokenData?: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData[]>;
    featuresAvailability?: JuspaySdkFeatureAvailable;
}

export const FETCH_TOKEN_ERROR = "Received empty data while fetching juspay client auth token";

export const useJuspaySdkState = (
    tenant: string,
    merchantId?: GetPaymentMethodsResponse["juspayMeta"]["merchantId"],
    // this optional param needs to be passed to iOS when native UPI or Cred instruments exist
    forceEnableSDK?: boolean,
    /** When true, uses the V3 multi-tenant auth tokens API; falls back to V2 when false */
    isV3Enabled?: boolean,
): JuspayContextValue => {
    const isMountedRef = useMountedRef();
    const juspayStateKey = isBusinessLineSwiggyMoney() ? SWIGGY_MONEY_JUSPAY_SDK_STATE : DEFAULT_JUSPAY_SDK_STATE;
    const [state, setState] = useSessionStorage<JuspaySdkState>(juspayStateKey, {});
    const [isInitialiseSdkAttempted, setInitialiseSdkAttempted] = useState(false);
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();
    const fetchRef = useRef(false);
    const [fetchingJuspaySdk, setFetchingJuspaySdk] = useState(false);

    const getJuspayClientAuthTokenFn = getPublicJuspayClientAuthToken;

    /** Fetches raw auth tokens from V3 (or V2). No SDK token selection — just returns the map and tenant token. */
    const fetchAuthTokens = useCallback(async (): Promise<
        JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData[]>
    > => {
        if (isV3Enabled) {
            const allAuthTokens = await getPublicJuspayClientAuthTokensV3({ apiHost, tenants: tenant });
            return allAuthTokens;
        }
        const clientAuthTokenData = await getJuspayClientAuthTokenFn({ apiHost, tenant });
        return clientAuthTokenData;
    }, [apiHost, getJuspayClientAuthTokenFn, isV3Enabled, tenant]);

    const checkIfSDKAvailable = useCallback(
        async (
            allAuthTokens: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData[]>,
        ): Promise<{
            isSdkAvailable: boolean;
            featuresAvailability?: JuspaySdkFeatureAvailable;
        }> => {
            const startTime = Date.now();
            const sdkTokenData = resolveClientAuthTokenDataForMerchantId(allAuthTokens, merchantId ?? "");

            return isJuspaySdkAvailable(sdkTokenData as JuspayClientAuthTokenData, merchantId, forceEnableSDK, tenant)
                .catch((err) => {
                    captureError(err);
                    return { isSdkAvailable: false, featuresAvailability: undefined };
                })
                .then((data) => {
                    try {
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.JUSPAY_SDK_NOT_AVAILABLE,
                            merchantId: merchantId ?? "--",
                            tenant: tenant ?? "--",
                            forceEnableSDK: String(forceEnableSDK ?? false),
                            hasClientAuthToken: String(!isEmpty(sdkTokenData)),
                            isSdkAvailable: String(data.isSdkAvailable),
                            featuresAvailability: data.featuresAvailability
                                ? JSON.stringify(data.featuresAvailability)
                                : "--",
                            responseTime: Date.now() - startTime,
                        });
                    } catch (telemetryError) {
                        captureError(telemetryError);
                    }
                    return data;
                });
        },
        [forceEnableSDK, merchantId, tenant],
    );

    const initializeSdk = useCallback(async () => {
        fetchRef.current = true;
        setInitialiseSdkAttempted(true);

        try {
            setFetchingJuspaySdk(true);
            const clientAuthTokenData = await fetchAuthTokens();

            const { isSdkAvailable, featuresAvailability } = await checkIfSDKAvailable(clientAuthTokenData);
            setState({
                isSdkAvailable,
                clientAuthTokenData,
                featuresAvailability,
            });
            setFetchingJuspaySdk(false);
            fetchRef.current = false;
        } catch (err) {
            fetchRef.current = false;
            setFetchingJuspaySdk(false);
            // (5.1) Juspay SDK init failure — the entire Juspay-based payment path becomes unusable silently
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_JUSPAY_CARD_FLOW_ERROR,
                state: WP_JUSPAY_CARD_FLOW_ERROR_STATE.JUSPAY_SDK_INIT_ERROR,
                errorMessage: (err as Error)?.message ?? String(err ?? "unknown"),
                merchantId: merchantId ?? "--",
            });
            captureError(err);
        }
        // disabled lint because setState of useSessionStorage should not be present
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkIfSDKAvailable, fetchAuthTokens]);

    const fetchJuspayClientAuthToken = useCallback(async () => {
        try {
            const clientAuthTokenData = await fetchAuthTokens();

            const { isSdkAvailable, featuresAvailability } = await checkIfSDKAvailable(clientAuthTokenData);
            setState({
                isSdkAvailable,
                clientAuthTokenData,
                featuresAvailability,
            });

            return clientAuthTokenData;
        } catch (err) {
            captureError(err);
        }
    }, [checkIfSDKAvailable, fetchAuthTokens]);

    useEffect(() => {
        if (!isMountedRef.current || fetchRef.current || !isEmpty(state) || isInitialiseSdkAttempted) {
            return;
        }
        initializeSdk();
    }, [initializeSdk, fetchRef, isMountedRef, state, isInitialiseSdkAttempted]);

    return { ...state, isSdkAvailable: !!state.isSdkAvailable, fetchingJuspaySdk, fetchJuspayClientAuthToken };
};
